import React, { useState, useRef } from 'react';
import { Button, Box, Typography } from '@mui/material';

import { Close, Description, Upload } from '@mui/icons-material';

type FileUploadProps = {
  /**
   * Callback when a file is selected
   */
  onFileSelect: (file: File | null) => void;

  /**
   * Currently selected file
   */
  selectedFile: File | null;

  /**
   * Allowed MIME types (e.g., ['text/csv', 'application/pdf'])
   */
  acceptedTypes?: string[];

  /**
   * File input accept attribute (e.g., '.csv,.xlsx,.pdf')
   */
  accept?: string;

  /**
   * Maximum file size in bytes
   */
  maxSize?: number;

  /**
   * Called when file validation fails
   */
  onValidationError?: (error: { type: 'invalid-type' | 'file-too-large'; message: string }) => void;

  /**
   * Custom placeholder content when no file is selected
   */
  placeholder?: React.ReactNode;

  /**
   * Custom label for drop zone
   */
  dropLabel?: string;

  /**
   * Custom label for click to browse
   */
  browseLabel?: string;

  /**
   * Display max file size hint
   */
  maxSizeLabel?: string;

  /**
   * Remove file button label
   */
  removeLabel?: string;

  /**
   * Disable the file upload
   */
  disabled?: boolean;

  /**
   * Show file size
   */
  showFileSize?: boolean;
};

/**
 * Reusable file upload component with drag-and-drop support
 *
 * Example:
 * ```tsx
 * const [file, setFile] = useState<File | null>(null);
 *
 * <FileUpload
 *   selectedFile={file}
 *   onFileSelect={setFile}
 *   acceptedTypes={['text/csv', 'application/pdf']}
 *   accept=".csv,.pdf"
 *   maxSize={10 * 1024 * 1024} // 10MB
 *   onValidationError={(error) => toast.error(error.message)}
 *   dropLabel="Drop your file here"
 *   browseLabel="or click to browse"
 *   maxSizeLabel="Max file size: 10MB"
 *   removeLabel="Remove file"
 * />
 * ```
 */
export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  selectedFile,
  acceptedTypes = [],
  accept,
  maxSize,
  onValidationError,
  placeholder,
  dropLabel = 'Drop file here',
  browseLabel = 'or click to browse',
  maxSizeLabel,
  removeLabel = 'Remove',
  disabled = false,
  showFileSize = true,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): boolean => {
    // Validate file type
    if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
      onValidationError?.({
        type: 'invalid-type',
        message: `Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`,
      });
      return false;
    }

    // Validate file size
    if (maxSize && file.size > maxSize) {
      onValidationError?.({
        type: 'file-too-large',
        message: `File size exceeds maximum allowed size of ${formatFileSize(maxSize)}`,
      });
      return false;
    }

    return true;
  };

  const handleFileSelection = (file: File) => {
    if (validateFile(file)) {
      onFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) {
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box
      sx={{
        border: '2px dashed',
        borderColor: dragActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        bgcolor: (() => {
          if (selectedFile) {
            return 'action.hover';
          }
          return dragActive ? 'action.selected' : 'transparent';
        })(),
        transition: 'all 0.2s ease',
        ...(!disabled && {
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }),
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {selectedFile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <Description sx={{ fontSize: 48 }} style={{ color: 'var(--mui-palette-text-secondary)' }} />
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {selectedFile.name}
            </Typography>
            {showFileSize && (
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRemove}
            disabled={disabled}
            type="button"
            startIcon={<Close sx={{ fontSize: 16 }} />}
          >
            {removeLabel}
          </Button>
        </Box>
      ) : (
        placeholder || (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Upload sx={{ fontSize: 48 }} style={{ color: 'var(--mui-palette-text-secondary)' }} />
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {dropLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {browseLabel}
              </Typography>
            </Box>
            {maxSizeLabel && (
              <Typography variant="caption" color="text.secondary">
                {maxSizeLabel}
              </Typography>
            )}
          </Box>
        )
      )}
    </Box>
  );
};
